import { api } from '@/lib/api';
import { ensureArray, safeParseFloat, sanitizeString } from './utils/validation';
import type {
  Zone,
  ZonesResponse,
  Location,
  LocationsResponse,
  LocationSummary,
  InventoryItem,
  InventoryResponse,
  InventorySummary,
  SerializedItem,
  SerializedItemsResponse,
  SerializedItemsSummary,
  StockMovement,
  MovementsResponse,
  MovementSummary,
  MongoSkuSearchResult,
  WorkOrder,
  WorkOrdersResponse,
  WorkOrderItem,
  WorkOrderSerial,
  WorkOrderSummary,
  PickingList,
  PickingListItem,
  PickingListsResponse,
  PickingSummary,
  PackingSlip,
  PackingSlipItem,
  PackingSlipsResponse,
  ShippingSummary,
  PackageDimensions,
  ShippingAddress,
  ContactInfo,
  ReceivingOrder,
  ReceivingLine,
  ReceivingSerial,
  ReceivingOrdersResponse,
  ReceivingSummary,
  CompleteReceivingResult,
  CreateZoneInput,
  UpdateZoneInput,
  CreateLocationInput,
  UpdateLocationInput,
  CreateInventoryInput,
  UpdateInventoryInput,
  ReceiveInventoryInput,
  TransferInventoryInput,
  AdjustInventoryInput,
  CreateManualInventoryInput,
  ExistingInventoryCheck,
  CreateSerializedItemInput,
  UpdateSerializedItemInput,
  ChangeDeviceStatusInput,
  CreateWorkOrderInput,
  UpdateWorkOrderInput,
  AddWorkOrderItemInput,
  UpdateWorkOrderItemInput,
  AssignSerialInput,
  CreateFromQuoteInput,
  GeneratePickingListInput,
  AssignPickerInput,
  ConfirmPickInput,
  ReportShortageInput,
  SkipItemInput,
  CreatePackingSlipInput,
  UpdatePackingSlipInput,
  UpdatePackingSlipItemInput,
  ShipPackingSlipInput,
  CreateReceivingOrderInput,
  UpdateReceivingOrderInput,
  AddReceivingLineInput,
  UpdateReceivingLineInput,
  ReceiveLineInput,
  UpdateQCStatusInput,
  InventoryFilters,
  SerializedItemFilters,
  MovementFilters,
  WorkOrderFilters,
  PickingListFilters,
  PackingSlipFilters,
  ReceivingFilters,
  WmsUsersResponse,
  WmsUserFilters,
  Supplier,
  SuppliersResponse,
  SupplierFilters,
  CreateSupplierInput,
  UpdateSupplierInput,
  Sku,
  SkuFilters,
  SkusResponse,
  CreateSkuInput,
  UpdateSkuInput,
  SkuSearchResult,
  SkuPhoto,
} from './types';

// =============================================================================
// V4 API Response Types (camelCase from server)
// =============================================================================

interface V4ZonesResponse {
  zones: V4Zone[];
  total: number;
}

interface V4Zone {
  id: string;
  code: string;
  name: string;
  zoneType: string;
  color?: string;
  description?: string;
  photoStoragePath?: string | null;
  pickPriority?: number;
  createdAt: string;
  updatedAt: string;
}

interface V4LocationsResponse {
  locations: V4Location[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4Location {
  id: string;
  zoneId: string;
  binCode: string;
  name?: string;
  binType: string;
  status: string;
  capacityUnits?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  zoneCode?: string;
  zoneName?: string;
  zoneColor?: string;
  fullCode?: string;
}

interface V4LocationSummary {
  zones: Array<{
    zoneId: string;
    zoneCode: string;
    zoneName: string;
    zoneType: string;
    zoneColor?: string;
    locationCount: number;
    activeCount: number;
  }>;
  totals: {
    totalZones: number;
    totalLocations: number;
    activeLocations: number;
  };
}

interface V4InventoryResponse {
  items: V4InventoryItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4InventoryItem {
  id: string;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  locationId: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  reorderPoint?: number;
  reorderQuantity?: number;
  lotNumber?: string;
  expirationDate?: string;
  unitCost?: string;
  unitOfMeasure?: string;
  itemStatus: string;
  lastCountedAt?: string;
  createdAt: string;
  updatedAt: string;
  binCode?: string;
  binType?: string;
  zoneId?: string;
  zoneCode?: string;
  zoneName?: string;
  zoneColor?: string;
  fullLocationCode?: string;
  isLowStock?: boolean;
  preferredSupplierId?: string;
  preferredSupplierName?: string;
  skuDetails?: {
    _id: string;
    sku: string;
    name?: { en?: string; fr?: string };
    manufacturer?: string;
    model?: string;
  };
}

interface V4InventorySummary {
  totals: {
    totalItems: number;
    totalQuantity: number;
    totalReserved: number;
    totalAvailable: number;
    totalValue?: string;
    lowStockCount: number;
    uniqueSkus: number;
  };
  byStatus: Array<{
    status: string;
    count: number;
    quantity: number;
  }>;
  byZone: Array<{
    zoneId: string;
    zoneCode: string;
    zoneName: string;
    zoneColor?: string;
    itemCount: number;
    totalQuantity: number;
  }>;
  lowStockItems: V4InventoryItem[];
}

interface V4SerializedItemsResponse {
  items: V4SerializedItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4SerializedItem {
  id: string;
  serialNumber: string;
  macAddress?: string;
  devId?: string;
  mongoSkuId: string;
  skuCode: string;
  mongoTransmitterId?: string;
  mongoSensorId?: string;
  inventoryItemId?: string;
  locationId: string;
  itemStatus: string;
  deviceStatus?: string;
  calibrationDueDate?: string;
  lastCalibrationDate?: string;
  manufactureDate?: string;
  firmwareVersion?: string;
  hardwareVersion?: string;
  workOrderId?: string;
  packingSlipId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  binCode?: string;
  zoneCode?: string;
  zoneName?: string;
  fullLocationCode?: string;
}

interface V4MovementsResponse {
  movements: V4Movement[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4Movement {
  id: string;
  movementType: string;
  inventoryItemId?: string;
  serializedItemId?: string;
  mongoSkuId: string;
  skuCode: string;
  quantity: number;
  fromLocationId?: string;
  toLocationId?: string;
  workOrderId?: string;
  packingSlipId?: string;
  purchaseOrderNumber?: string;
  referenceNumber?: string;
  reasonCode?: string;
  notes?: string;
  performedBy: string;
  performedByName?: string;
  performedAt: string;
  createdAt: string;
}

interface V4MovementSummary {
  byType: Array<{
    movementType: string;
    count: number;
    totalQuantity: number;
  }>;
  byDay: Array<{
    date: string;
    count: number;
    receives: number;
    transfers: number;
    adjustments: number;
  }>;
  recentMovements: V4Movement[];
  period: {
    from: string;
    to: string;
  };
}

interface V4SerializedItemsSummary {
  totals: {
    totalItems: number;
    uniqueSkus: number;
    inStockCount: number;
    reservedCount: number;
    shippedCount: number;
    installedCount: number;
    rmaCount: number;
    calibrationDueCount: number;
  };
  byItemStatus: Array<{
    status: string;
    count: number;
  }>;
  byDeviceStatus: Array<{
    status: string;
    count: number;
  }>;
  byZone: Array<{
    zoneId: string;
    zoneCode: string;
    zoneName: string;
    zoneColor?: string;
    itemCount: number;
  }>;
  calibrationDueItems: V4SerializedItem[];
  recentItems: V4SerializedItem[];
}

// =============================================================================
// Transform Functions (camelCase API → snake_case internal)
// =============================================================================

function transformZone(v4: V4Zone): Zone {
  return {
    id: v4.id,
    code: v4.code,
    name: v4.name,
    zone_type: v4.zoneType as Zone['zone_type'],
    color: v4.color,
    description: v4.description,
    photo_storage_path: v4.photoStoragePath,
    pick_priority: v4.pickPriority,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

function transformLocation(v4: V4Location): Location {
  return {
    id: v4.id,
    zone_id: v4.zoneId,
    bin_code: v4.binCode,
    name: v4.name,
    bin_type: v4.binType as Location['bin_type'],
    status: v4.status as Location['status'],
    capacity_units: v4.capacityUnits,
    notes: v4.notes,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    zone_code: v4.zoneCode,
    zone_name: v4.zoneName,
    zone_color: v4.zoneColor,
    full_code: v4.fullCode,
  };
}

function transformInventoryItem(v4: V4InventoryItem): InventoryItem {
  return {
    id: v4.id,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    location_id: v4.locationId,
    quantity_on_hand: v4.quantityOnHand || 0,
    quantity_reserved: v4.quantityReserved || 0,
    quantity_available: v4.quantityAvailable || 0,
    reorder_point: v4.reorderPoint,
    reorder_quantity: v4.reorderQuantity,
    lot_number: v4.lotNumber,
    expiration_date: v4.expirationDate,
    unit_cost: safeParseFloat(v4.unitCost, { min: 0 }),
    unit_of_measure: v4.unitOfMeasure,
    item_status: v4.itemStatus as InventoryItem['item_status'],
    last_counted_at: v4.lastCountedAt,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    bin_code: v4.binCode,
    bin_type: v4.binType as InventoryItem['bin_type'],
    zone_id: v4.zoneId,
    zone_code: v4.zoneCode,
    zone_name: v4.zoneName,
    zone_color: v4.zoneColor,
    full_location_code: v4.fullLocationCode,
    is_low_stock: v4.isLowStock,
    preferred_supplier_id: v4.preferredSupplierId,
    preferred_supplier_name: v4.preferredSupplierName,
    sku_details: v4.skuDetails,
  };
}

function transformSerializedItem(v4: V4SerializedItem): SerializedItem {
  return {
    id: v4.id,
    serial_number: v4.serialNumber,
    mac_address: v4.macAddress,
    dev_id: v4.devId,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    mongo_transmitter_id: v4.mongoTransmitterId,
    mongo_sensor_id: v4.mongoSensorId,
    inventory_item_id: v4.inventoryItemId,
    location_id: v4.locationId,
    item_status: v4.itemStatus as SerializedItem['item_status'],
    device_status: v4.deviceStatus as SerializedItem['device_status'],
    calibration_due_date: v4.calibrationDueDate,
    last_calibration_date: v4.lastCalibrationDate,
    manufacture_date: v4.manufactureDate,
    firmware_version: v4.firmwareVersion,
    hardware_version: v4.hardwareVersion,
    work_order_id: v4.workOrderId,
    packing_slip_id: v4.packingSlipId,
    notes: v4.notes,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    bin_code: v4.binCode,
    zone_code: v4.zoneCode,
    zone_name: v4.zoneName,
    full_location_code: v4.fullLocationCode,
  };
}

function transformMovement(v4: V4Movement): StockMovement {
  return {
    id: v4.id,
    movement_type: v4.movementType as StockMovement['movement_type'],
    inventory_item_id: v4.inventoryItemId,
    serialized_item_id: v4.serializedItemId,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    quantity: v4.quantity,
    from_location_id: v4.fromLocationId,
    to_location_id: v4.toLocationId,
    work_order_id: v4.workOrderId,
    packing_slip_id: v4.packingSlipId,
    purchase_order_number: v4.purchaseOrderNumber,
    reference_number: v4.referenceNumber,
    reason_code: v4.reasonCode as StockMovement['reason_code'],
    notes: v4.notes,
    performed_by: v4.performedBy,
    performed_by_name: v4.performedByName,
    performed_at: v4.performedAt,
    created_at: v4.createdAt,
  };
}

// =============================================================================
// API Functions - Zones
// =============================================================================

export async function getZones(): Promise<ZonesResponse> {
  const response = await api.get<V4ZonesResponse>('/v4/wms/zones');
  return {
    zones: ensureArray(response.zones).map(transformZone),
    total: response.total || 0,
  };
}

export async function getZone(id: string): Promise<Zone> {
  const response = await api.get<{ zone: V4Zone }>(`/v4/wms/zones/${id}`);
  return transformZone(response.zone);
}

export async function createZone(input: CreateZoneInput): Promise<Zone> {
  const response = await api.post<{ zone: V4Zone }>('/v4/wms/zones', {
    code: input.code,
    name: input.name,
    zoneType: input.zone_type,
    color: input.color,
    description: input.description,
    pickPriority: input.pick_priority,
  });
  return transformZone(response.zone);
}

export async function updateZone(id: string, input: UpdateZoneInput): Promise<Zone> {
  const response = await api.put<{ zone: V4Zone }>(`/v4/wms/zones/${id}`, {
    name: input.name,
    zoneType: input.zone_type,
    color: input.color,
    description: input.description,
    pickPriority: input.pick_priority,
  });
  return transformZone(response.zone);
}

export async function deleteZone(id: string): Promise<void> {
  await api.delete(`/v4/wms/zones/${id}`);
}

export async function uploadZonePhoto(zoneId: string, file: File): Promise<Zone> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.upload<{ zone: V4Zone }>(`/v4/wms/zones/${zoneId}/photo`, formData);
  return transformZone(response.zone);
}

export async function deleteZonePhoto(zoneId: string): Promise<void> {
  await api.delete(`/v4/wms/zones/${zoneId}/photo`);
}

export function getZonePhotoUrl(zoneId: string): string {
  return `/api/v4/wms/zones/${zoneId}/photo`;
}

// =============================================================================
// API Functions - Locations
// =============================================================================

export async function getLocations(filters?: { zoneId?: string; status?: string; search?: string; pageNumber?: number; pageLength?: number }): Promise<LocationsResponse> {
  const response = await api.get<V4LocationsResponse>('/v4/wms/locations', filters);
  return {
    locations: ensureArray(response.locations).map(transformLocation),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getLocationSummary(): Promise<LocationSummary> {
  const response = await api.get<V4LocationSummary>('/v4/wms/locations/summary');
  return {
    zones: ensureArray(response.zones).map(z => ({
      zone_id: z.zoneId,
      zone_code: z.zoneCode,
      zone_name: z.zoneName,
      zone_type: z.zoneType as LocationSummary['zones'][0]['zone_type'],
      zone_color: z.zoneColor,
      location_count: z.locationCount || 0,
      active_count: z.activeCount || 0,
    })),
    totals: {
      total_zones: response.totals?.totalZones || 0,
      total_locations: response.totals?.totalLocations || 0,
      active_locations: response.totals?.activeLocations || 0,
    },
  };
}

export async function getLocation(id: string): Promise<Location> {
  const response = await api.get<{ location: V4Location }>(`/v4/wms/locations/${id}`);
  return transformLocation(response.location);
}

export async function createLocation(input: CreateLocationInput): Promise<Location> {
  const response = await api.post<{ location: V4Location }>('/v4/wms/locations', {
    zoneId: input.zone_id,
    binCode: input.bin_code,
    name: input.name,
    binType: input.bin_type,
    status: input.status,
    capacityUnits: input.capacity_units,
    notes: input.notes,
  });
  return transformLocation(response.location);
}

export async function updateLocation(id: string, input: UpdateLocationInput): Promise<Location> {
  const response = await api.put<{ location: V4Location }>(`/v4/wms/locations/${id}`, {
    binCode: input.bin_code,
    name: input.name,
    binType: input.bin_type,
    status: input.status,
    capacityUnits: input.capacity_units,
    notes: input.notes,
  });
  return transformLocation(response.location);
}

export async function deleteLocation(id: string): Promise<void> {
  await api.delete(`/v4/wms/locations/${id}`);
}

export async function bulkCreateLocations(zoneId: string, binCodes: string[]): Promise<{ created: number; locations: Location[] }> {
  const response = await api.post<{ created: number; locations: V4Location[] }>('/v4/wms/locations/bulk', {
    zoneId,
    binCodes,
  });
  return {
    created: response.created || 0,
    locations: ensureArray(response.locations).map(transformLocation),
  };
}

// =============================================================================
// API Functions - Inventory
// =============================================================================

export async function getInventory(filters?: InventoryFilters): Promise<InventoryResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.location_id) params.locationId = filters.location_id;
  if (filters?.zone_id) params.zoneId = filters.zone_id;
  if (filters?.mongo_sku_id) params.mongoSkuId = filters.mongo_sku_id;
  if (filters?.sku_code) {
    const sanitized = sanitizeString(filters.sku_code, 50);
    if (sanitized) params.skuCode = sanitized;
  }
  if (filters?.preferred_supplier_id) params.preferredSupplierId = filters.preferred_supplier_id;
  if (filters?.item_status) params.itemStatus = filters.item_status;
  if (filters?.low_stock) params.lowStock = 'true';
  if (filters?.search) {
    const sanitized = sanitizeString(filters.search, 100);
    if (sanitized) params.search = sanitized;
  }
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction !== undefined) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4InventoryResponse>('/v4/wms/inventory', params);
  return {
    items: ensureArray(response.items).map(transformInventoryItem),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getInventorySummary(): Promise<InventorySummary> {
  const response = await api.get<V4InventorySummary>('/v4/wms/inventory/summary');
  return {
    totals: {
      total_items: response.totals?.totalItems || 0,
      total_quantity: response.totals?.totalQuantity || 0,
      total_reserved: response.totals?.totalReserved || 0,
      total_available: response.totals?.totalAvailable || 0,
      total_value: safeParseFloat(response.totals?.totalValue, { min: 0 }),
      low_stock_count: response.totals?.lowStockCount || 0,
      unique_skus: response.totals?.uniqueSkus || 0,
    },
    by_status: ensureArray(response.byStatus).map(s => ({
      status: s.status as InventorySummary['by_status'][0]['status'],
      count: s.count || 0,
      quantity: s.quantity || 0,
    })),
    by_zone: ensureArray(response.byZone).map(z => ({
      zone_id: z.zoneId,
      zone_code: z.zoneCode,
      zone_name: z.zoneName,
      zone_color: z.zoneColor,
      item_count: z.itemCount || 0,
      total_quantity: z.totalQuantity || 0,
    })),
    low_stock_items: ensureArray(response.lowStockItems).map(transformInventoryItem),
  };
}

export async function getInventoryTotalsBySkus(
  mongoSkuIds: string[],
): Promise<Record<string, { quantityOnHand: number; quantityReserved: number; quantityAvailable: number }>> {
  if (mongoSkuIds.length === 0) return {};
  const response = await api.get<{
    totals: Record<string, { quantityOnHand: number; quantityReserved: number; quantityAvailable: number }>;
  }>('/v4/wms/inventory/sku-totals', { mongoSkuIds: mongoSkuIds.join(',') });
  return response.totals || {};
}

export async function getInventoryItem(id: string): Promise<InventoryItem> {
  const response = await api.get<{ item: V4InventoryItem }>(`/v4/wms/inventory/${id}`);
  return transformInventoryItem(response.item);
}

export async function createInventoryItem(input: CreateInventoryInput): Promise<InventoryItem> {
  const response = await api.post<{ item: V4InventoryItem }>('/v4/wms/inventory', {
    mongoSkuId: input.mongo_sku_id,
    skuCode: input.sku_code,
    skuName: input.sku_name,
    locationId: input.location_id,
    quantityOnHand: input.quantity_on_hand,
    reorderPoint: input.reorder_point,
    reorderQuantity: input.reorder_quantity,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    itemStatus: input.item_status,
  });
  return transformInventoryItem(response.item);
}

export async function deleteInventoryItem(id: string): Promise<void> {
  await api.delete(`/v4/wms/inventory/${id}`);
}

export async function updateInventoryItem(id: string, input: UpdateInventoryInput): Promise<InventoryItem> {
  const response = await api.put<{ item: V4InventoryItem }>(`/v4/wms/inventory/${id}`, {
    skuName: input.sku_name,
    locationId: input.location_id,
    reorderPoint: input.reorder_point,
    reorderQuantity: input.reorder_quantity,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    unitOfMeasure: input.unit_of_measure,
    itemStatus: input.item_status,
    preferredSupplierId: input.preferred_supplier_id,
    preferredSupplierName: input.preferred_supplier_name,
  });
  return transformInventoryItem(response.item);
}

export async function searchSkus(search?: string, limit?: number): Promise<MongoSkuSearchResult[]> {
  const response = await api.get<{ skus: MongoSkuSearchResult[] }>('/v4/wms/inventory/skus', { search, limit });
  return response.skus;
}

// =============================================================================
// API Functions - Stock Movements
// =============================================================================

export async function getMovements(filters?: MovementFilters): Promise<MovementsResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.movement_type) params.movementType = filters.movement_type;
  if (filters?.mongo_sku_id) params.mongoSkuId = filters.mongo_sku_id;
  if (filters?.inventory_item_id) params.inventoryItemId = filters.inventory_item_id;
  if (filters?.serialized_item_id) params.serializedItemId = filters.serialized_item_id;
  if (filters?.from_location_id) params.fromLocationId = filters.from_location_id;
  if (filters?.to_location_id) params.toLocationId = filters.to_location_id;
  if (filters?.performed_by) params.performedBy = filters.performed_by;
  if (filters?.reason_code) params.reasonCode = filters.reason_code;
  if (filters?.from_date) params.fromDate = filters.from_date;
  if (filters?.to_date) params.toDate = filters.to_date;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;

  const response = await api.get<V4MovementsResponse>('/v4/wms/movements', params);
  return {
    movements: ensureArray(response.movements).map(transformMovement),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getMovementSummary(params?: { fromDate?: string; toDate?: string }): Promise<MovementSummary> {
  const response = await api.get<V4MovementSummary>('/v4/wms/movements/summary', params);
  return {
    by_type: ensureArray(response.byType).map(t => ({
      movement_type: t.movementType as MovementSummary['by_type'][0]['movement_type'],
      count: t.count || 0,
      total_quantity: t.totalQuantity || 0,
    })),
    by_day: ensureArray(response.byDay).map(d => ({
      date: d.date,
      count: d.count || 0,
      receives: d.receives || 0,
      transfers: d.transfers || 0,
      adjustments: d.adjustments || 0,
    })),
    recent_movements: ensureArray(response.recentMovements).map(transformMovement),
    period: response.period || { from: '', to: '' },
  };
}

export async function receiveInventory(input: ReceiveInventoryInput): Promise<{ inventoryItemId: string; quantity: number }> {
  const response = await api.post<{ inventoryItemId: string; quantity: number }>('/v4/wms/movements/receive', {
    locationId: input.location_id,
    mongoSkuId: input.mongo_sku_id,
    skuCode: input.sku_code,
    skuName: input.sku_name,
    quantity: input.quantity,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    itemStatus: input.item_status,
    purchaseOrderNumber: input.purchase_order_number,
    referenceNumber: input.reference_number,
    notes: input.notes,
  });
  return response;
}

export async function transferInventory(input: TransferInventoryInput): Promise<{ sourceInventoryItemId: string; destInventoryItemId: string; quantity: number }> {
  const response = await api.post<{ sourceInventoryItemId: string; destInventoryItemId: string; quantity: number }>('/v4/wms/movements/transfer', {
    inventoryItemId: input.inventory_item_id,
    toLocationId: input.to_location_id,
    quantity: input.quantity,
    referenceNumber: input.reference_number,
    notes: input.notes,
  });
  return response;
}

export async function adjustInventory(input: AdjustInventoryInput): Promise<{ inventoryItemId: string; previousQuantity: number; newQuantity: number; difference: number }> {
  const response = await api.post<{ inventoryItemId: string; previousQuantity: number; newQuantity: number; difference: number }>('/v4/wms/movements/adjust', {
    inventoryItemId: input.inventory_item_id,
    newQuantity: input.new_quantity,
    reasonCode: input.reason_code,
    referenceNumber: input.reference_number,
    notes: input.notes,
  });
  return response;
}

export async function createManualInventoryItem(input: CreateManualInventoryInput): Promise<InventoryItem> {
  const response = await api.post<{ item: InventoryItem; movement: unknown }>('/v4/wms/inventory/manual', {
    name: input.name,
    locationId: input.location_id,
    quantity: input.quantity,
    mongoSkuId: input.mongo_sku_id,
    createNewSku: input.create_new_sku,
    skuCode: input.sku_code,
    supplierName: input.supplier_name,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    unitOfMeasure: input.unit_of_measure,
    notes: input.notes,
  });
  return response.item;
}

export async function checkExistingInventory(mongoSkuId: string, locationId: string): Promise<ExistingInventoryCheck | null> {
  const response = await api.get<{ existing: ExistingInventoryCheck | null }>('/v4/wms/inventory/check-existing', {
    mongoSkuId,
    locationId,
  });
  return response.existing;
}

// =============================================================================
// API Functions - Serialized Items
// =============================================================================

export async function getSerializedItems(filters?: SerializedItemFilters): Promise<SerializedItemsResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.location_id) params.locationId = filters.location_id;
  if (filters?.zone_id) params.zoneId = filters.zone_id;
  if (filters?.mongo_sku_id) params.mongoSkuId = filters.mongo_sku_id;
  if (filters?.item_status) params.itemStatus = filters.item_status;
  if (filters?.device_status) params.deviceStatus = filters.device_status;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;

  const response = await api.get<V4SerializedItemsResponse>('/v4/wms/serials', params);
  return {
    items: ensureArray(response.items).map(transformSerializedItem),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getSerializedItem(id: string): Promise<SerializedItem> {
  const response = await api.get<{ item: V4SerializedItem }>(`/v4/wms/serials/${id}`);
  return transformSerializedItem(response.item);
}

export async function lookupSerial(serialNumber: string): Promise<SerializedItem> {
  const response = await api.get<{ item: V4SerializedItem }>(`/v4/wms/serials/lookup/${encodeURIComponent(serialNumber)}`);
  return transformSerializedItem(response.item);
}

export async function createSerializedItem(input: CreateSerializedItemInput): Promise<SerializedItem> {
  const response = await api.post<{ item: V4SerializedItem }>('/v4/wms/serials', {
    serialNumber: input.serial_number,
    mongoSkuId: input.mongo_sku_id,
    skuCode: input.sku_code,
    locationId: input.location_id,
    macAddress: input.mac_address,
    devId: input.dev_id,
    deviceStatus: input.device_status,
    calibrationDueDate: input.calibration_due_date,
    firmwareVersion: input.firmware_version,
    hardwareVersion: input.hardware_version,
    notes: input.notes,
  });
  return transformSerializedItem(response.item);
}

export async function transferSerializedItem(id: string, locationId: string): Promise<SerializedItem> {
  const response = await api.post<{ item: V4SerializedItem; transfer: { fromLocationId: string; toLocationId: string } }>(`/v4/wms/serials/${id}/transfer`, {
    locationId,
  });
  return transformSerializedItem(response.item);
}

export async function updateSerializedItem(id: string, input: UpdateSerializedItemInput): Promise<SerializedItem> {
  const response = await api.put<{ item: V4SerializedItem }>(`/v4/wms/serials/${id}`, {
    macAddress: input.mac_address,
    devId: input.dev_id,
    deviceStatus: input.device_status,
    calibrationDueDate: input.calibration_due_date,
    lastCalibrationDate: input.last_calibration_date,
    firmwareVersion: input.firmware_version,
    hardwareVersion: input.hardware_version,
    notes: input.notes,
  });
  return transformSerializedItem(response.item);
}

export async function changeDeviceStatus(id: string, input: ChangeDeviceStatusInput): Promise<{ item: SerializedItem; status_change: { previous_status: string | null; new_status: string; reason: string; notes?: string } }> {
  const response = await api.put<{ item: V4SerializedItem; statusChange: { previousStatus: string | null; newStatus: string; reason: string; notes?: string } }>(`/v4/wms/serials/${id}/status`, {
    deviceStatus: input.device_status,
    reason: input.reason,
    notes: input.notes,
  });
  return {
    item: transformSerializedItem(response.item),
    status_change: {
      previous_status: response.statusChange.previousStatus,
      new_status: response.statusChange.newStatus,
      reason: response.statusChange.reason,
      notes: response.statusChange.notes,
    },
  };
}

export async function getSerializedItemsSummary(): Promise<SerializedItemsSummary> {
  const response = await api.get<V4SerializedItemsSummary>('/v4/wms/serials/summary');
  return {
    totals: {
      total_items: response.totals?.totalItems || 0,
      unique_skus: response.totals?.uniqueSkus || 0,
      in_stock_count: response.totals?.inStockCount || 0,
      reserved_count: response.totals?.reservedCount || 0,
      shipped_count: response.totals?.shippedCount || 0,
      installed_count: response.totals?.installedCount || 0,
      rma_count: response.totals?.rmaCount || 0,
      calibration_due_count: response.totals?.calibrationDueCount || 0,
    },
    by_item_status: ensureArray(response.byItemStatus).map(s => ({
      status: s.status as SerializedItemsSummary['by_item_status'][0]['status'],
      count: s.count || 0,
    })),
    by_device_status: ensureArray(response.byDeviceStatus).map(s => ({
      status: s.status as SerializedItemsSummary['by_device_status'][0]['status'],
      count: s.count || 0,
    })),
    by_zone: ensureArray(response.byZone).map(z => ({
      zone_id: z.zoneId,
      zone_code: z.zoneCode,
      zone_name: z.zoneName,
      zone_color: z.zoneColor,
      item_count: z.itemCount || 0,
    })),
    calibration_due_items: ensureArray(response.calibrationDueItems).map(transformSerializedItem),
    recent_items: ensureArray(response.recentItems).map(transformSerializedItem),
  };
}

export async function getSerializedItemMovements(id: string, filters?: MovementFilters): Promise<MovementsResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;

  const response = await api.get<V4MovementsResponse>(`/v4/wms/serials/${id}/movements`, params);
  return {
    movements: ensureArray(response.movements).map(transformMovement),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

// =============================================================================
// V4 API Response Types - Work Orders (camelCase from server)
// =============================================================================

interface V4WorkOrdersResponse {
  items: V4WorkOrder[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4WorkOrder {
  id: string;
  workOrderNumber: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  opportunityId?: string;
  mongoQuoteId?: string;
  accountId?: string;
  accountName?: string;
  dueDate?: string;
  startedAt?: string;
  completedAt?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  notes?: string;
  createdByUserId: string;
  createdByUserName?: string;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
  totalOrdered?: number;
  totalAllocated?: number;
  totalPicked?: number;
  totalShipped?: number;
  totalValue?: string;
}

interface V4WorkOrderItem {
  id: string;
  workOrderId: string;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  quantityOrdered: number;
  quantityAllocated: number;
  quantityPicked: number;
  quantityShipped: number;
  unitPrice?: string;
  lineTotal?: string;
  itemStatus: string;
  notes?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  serialCount?: number;
  quantityRemaining?: number;
}

interface V4WorkOrderSerial {
  id: string;
  workOrderItemId: string;
  serializedItemId: string;
  serialNumber: string;
  allocationStatus: string;
  allocatedAt: string;
  allocatedByUserId?: string;
  pickedAt?: string;
  pickedByUserId?: string;
  shippedAt?: string;
  notes?: string;
  skuCode?: string;
  skuName?: string;
  macAddress?: string;
  devId?: string;
  deviceStatus?: string;
  binCode?: string;
  zoneCode?: string;
}

interface V4WorkOrderSummary {
  byStatus: Array<{ status: string; count: number }>;
  byPriority: Array<{ priority: string; count: number }>;
  overdueCount: number;
  pendingAllocation: { itemCount: number; totalQuantity: number };
  recentOrders: Array<{
    id: string;
    workOrderNumber: string;
    title: string;
    status: string;
    priority: string;
    accountName?: string;
    dueDate?: string;
    createdAt: string;
  }>;
}

// =============================================================================
// Transform Functions - Work Orders
// =============================================================================

function transformWorkOrder(v4: V4WorkOrder): WorkOrder {
  return {
    id: v4.id,
    work_order_number: v4.workOrderNumber,
    title: v4.title,
    description: v4.description,
    status: v4.status as WorkOrder['status'],
    priority: v4.priority as WorkOrder['priority'],
    opportunity_id: v4.opportunityId,
    mongo_quote_id: v4.mongoQuoteId,
    account_id: v4.accountId,
    account_name: v4.accountName,
    due_date: v4.dueDate,
    started_at: v4.startedAt,
    completed_at: v4.completedAt,
    assigned_to_user_id: v4.assignedToUserId,
    assigned_to_user_name: v4.assignedToUserName,
    notes: v4.notes,
    created_by_user_id: v4.createdByUserId,
    created_by_user_name: v4.createdByUserName,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    item_count: v4.itemCount,
    total_ordered: v4.totalOrdered || 0,
    total_allocated: v4.totalAllocated || 0,
    total_picked: v4.totalPicked || 0,
    total_shipped: v4.totalShipped || 0,
    total_value: safeParseFloat(v4.totalValue, { min: 0 }),
  };
}

function transformWorkOrderItem(v4: V4WorkOrderItem): WorkOrderItem {
  return {
    id: v4.id,
    work_order_id: v4.workOrderId,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    quantity_ordered: v4.quantityOrdered || 0,
    quantity_allocated: v4.quantityAllocated || 0,
    quantity_picked: v4.quantityPicked || 0,
    quantity_shipped: v4.quantityShipped || 0,
    unit_price: safeParseFloat(v4.unitPrice, { min: 0 }),
    line_total: safeParseFloat(v4.lineTotal, { min: 0 }),
    item_status: v4.itemStatus as WorkOrderItem['item_status'],
    notes: v4.notes,
    sort_order: v4.sortOrder || 0,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    serial_count: v4.serialCount || 0,
    quantity_remaining: v4.quantityRemaining,
  };
}

function transformWorkOrderSerial(v4: V4WorkOrderSerial): WorkOrderSerial {
  return {
    id: v4.id,
    work_order_item_id: v4.workOrderItemId,
    serialized_item_id: v4.serializedItemId,
    serial_number: v4.serialNumber,
    allocation_status: v4.allocationStatus as WorkOrderSerial['allocation_status'],
    allocated_at: v4.allocatedAt,
    allocated_by_user_id: v4.allocatedByUserId,
    picked_at: v4.pickedAt,
    picked_by_user_id: v4.pickedByUserId,
    shipped_at: v4.shippedAt,
    notes: v4.notes,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    mac_address: v4.macAddress,
    dev_id: v4.devId,
    device_status: v4.deviceStatus as WorkOrderSerial['device_status'],
    bin_code: v4.binCode,
    zone_code: v4.zoneCode,
  };
}

// =============================================================================
// API Functions - Work Orders
// =============================================================================

export async function getWorkOrders(filters?: WorkOrderFilters): Promise<WorkOrdersResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.priority) params.priority = filters.priority;
  if (filters?.account_id) params.accountId = filters.account_id;
  if (filters?.opportunity_id) params.opportunityId = filters.opportunity_id;
  if (filters?.assigned_to_user_id) params.assignedToUserId = filters.assigned_to_user_id;
  if (filters?.due_date_from) params.dueDateFrom = filters.due_date_from;
  if (filters?.due_date_to) params.dueDateTo = filters.due_date_to;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction !== undefined) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4WorkOrdersResponse>('/v4/wms/work-orders', params);
  return {
    items: ensureArray(response.items).map(transformWorkOrder),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getWorkOrder(id: string): Promise<WorkOrder> {
  const response = await api.get<V4WorkOrder>(`/v4/wms/work-orders/${id}`);
  return transformWorkOrder(response);
}

export async function getWorkOrderSummary(): Promise<WorkOrderSummary> {
  const response = await api.get<V4WorkOrderSummary>('/v4/wms/work-orders/summary');
  return {
    by_status: ensureArray(response.byStatus).map(s => ({
      status: s.status as WorkOrderSummary['by_status'][0]['status'],
      count: s.count || 0,
    })),
    by_priority: ensureArray(response.byPriority).map(p => ({
      priority: p.priority as WorkOrderSummary['by_priority'][0]['priority'],
      count: p.count || 0,
    })),
    overdue_count: response.overdueCount || 0,
    pending_allocation: {
      item_count: response.pendingAllocation?.itemCount || 0,
      total_quantity: response.pendingAllocation?.totalQuantity || 0,
    },
    recent_orders: ensureArray(response.recentOrders).map(o => ({
      id: o.id,
      work_order_number: o.workOrderNumber,
      title: o.title,
      status: o.status as WorkOrderSummary['recent_orders'][0]['status'],
      priority: o.priority as WorkOrderSummary['recent_orders'][0]['priority'],
      account_name: o.accountName,
      due_date: o.dueDate,
      created_at: o.createdAt,
    })),
  };
}

export async function createWorkOrder(input: CreateWorkOrderInput): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>('/v4/wms/work-orders', {
    title: input.title,
    description: input.description,
    priority: input.priority,
    opportunityId: input.opportunity_id,
    mongoQuoteId: input.mongo_quote_id,
    accountId: input.account_id,
    accountName: input.account_name,
    dueDate: input.due_date,
    assignedToUserId: input.assigned_to_user_id,
    assignedToUserName: input.assigned_to_user_name,
    supplierId: input.supplier_id,
    supplierName: input.supplier_name,
    notes: input.notes,
  });
  return transformWorkOrder(response);
}

export async function updateWorkOrder(id: string, input: UpdateWorkOrderInput): Promise<WorkOrder> {
  const response = await api.put<V4WorkOrder>(`/v4/wms/work-orders/${id}`, {
    title: input.title,
    description: input.description,
    priority: input.priority,
    dueDate: input.due_date,
    assignedToUserId: input.assigned_to_user_id,
    assignedToUserName: input.assigned_to_user_name,
    supplierId: input.supplier_id,
    supplierName: input.supplier_name,
    notes: input.notes,
  });
  return transformWorkOrder(response);
}

export async function deleteWorkOrder(id: string): Promise<void> {
  await api.delete(`/v4/wms/work-orders/${id}`);
}

export async function submitWorkOrder(id: string): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/${id}/submit`, {});
  return transformWorkOrder(response);
}

export async function confirmWorkOrder(id: string): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/${id}/confirm`, {});
  return transformWorkOrder(response);
}

export async function startWorkOrder(id: string): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/${id}/start`, {});
  return transformWorkOrder(response);
}

export async function completeWorkOrder(id: string): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/${id}/complete`, {});
  return transformWorkOrder(response);
}

export async function cancelWorkOrder(id: string, reason?: string): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/${id}/cancel`, { reason });
  return transformWorkOrder(response);
}

export async function createWorkOrderFromQuote(quoteId: string, input: CreateFromQuoteInput): Promise<WorkOrder> {
  const response = await api.post<V4WorkOrder>(`/v4/wms/work-orders/from-quote/${quoteId}`, {
    accountId: input.account_id,
    accountName: input.account_name,
  });
  return transformWorkOrder(response);
}

// =============================================================================
// API Functions - Work Order Items
// =============================================================================

export async function getWorkOrderItems(workOrderId: string): Promise<WorkOrderItem[]> {
  const response = await api.get<{ items: V4WorkOrderItem[] }>(`/v4/wms/work-orders/${workOrderId}/items`);
  return ensureArray(response.items).map(transformWorkOrderItem);
}

export async function addWorkOrderItem(workOrderId: string, input: AddWorkOrderItemInput): Promise<WorkOrderItem> {
  const response = await api.post<V4WorkOrderItem>(`/v4/wms/work-orders/${workOrderId}/items`, {
    mongoSkuId: input.mongo_sku_id,
    skuCode: input.sku_code,
    skuName: input.sku_name,
    quantityOrdered: input.quantity_ordered,
    unitPrice: input.unit_price,
    notes: input.notes,
    sortOrder: input.sort_order,
  });
  return transformWorkOrderItem(response);
}

export async function updateWorkOrderItem(workOrderId: string, itemId: string, input: UpdateWorkOrderItemInput): Promise<WorkOrderItem> {
  const response = await api.put<V4WorkOrderItem>(`/v4/wms/work-orders/${workOrderId}/items/${itemId}`, {
    quantityOrdered: input.quantity_ordered,
    unitPrice: input.unit_price,
    notes: input.notes,
    sortOrder: input.sort_order,
  });
  return transformWorkOrderItem(response);
}

export async function removeWorkOrderItem(workOrderId: string, itemId: string): Promise<void> {
  await api.delete(`/v4/wms/work-orders/${workOrderId}/items/${itemId}`);
}

// =============================================================================
// API Functions - Work Order Serial Allocations
// =============================================================================

export async function getWorkOrderSerials(workOrderId: string): Promise<WorkOrderSerial[]> {
  const response = await api.get<{ serials: V4WorkOrderSerial[] }>(`/v4/wms/work-orders/${workOrderId}/serials`);
  return ensureArray(response.serials).map(transformWorkOrderSerial);
}

export async function assignSerialToWorkOrder(workOrderId: string, input: AssignSerialInput): Promise<WorkOrderSerial> {
  const response = await api.post<V4WorkOrderSerial>(`/v4/wms/work-orders/${workOrderId}/serials`, {
    itemId: input.item_id,
    serializedItemId: input.serialized_item_id,
  });
  return transformWorkOrderSerial(response);
}

export async function unassignSerialFromWorkOrder(workOrderId: string, allocationId: string): Promise<void> {
  await api.delete(`/v4/wms/work-orders/${workOrderId}/serials/${allocationId}`);
}

// =============================================================================
// V4 API Response Types - Picking Lists (camelCase from server)
// =============================================================================

interface V4PickingListsResponse {
  items: V4PickingList[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4PickingList {
  id: string;
  pickingNumber: string;
  workOrderId: string;
  workOrderNumber?: string;
  status: string;
  priority: string;
  assignedTo?: string;
  assignedToName?: string;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
  itemsPicked?: number;
  items?: V4PickingListItem[];
}

interface V4PickingListItem {
  id: string;
  pickingListId: string;
  workOrderItemId: string;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  quantityToPick: number;
  quantityPicked: number;
  quantityShort: number;
  pickStatus: string;
  locationId?: string;
  binCode?: string;
  zoneCode?: string;
  zonePriority?: number;
  lotNumber?: string;
  serialNumbers?: string[];
  pickedAt?: string;
  pickedBy?: string;
  pickedByName?: string;
  shortageReason?: string;
  notes?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface V4PickingSummary {
  byStatus: Array<{ status: string; count: number }>;
  todayStats: {
    listsCreated: number;
    listsCompleted: number;
    itemsPicked: number;
    shortages: number;
  };
  activePickers: Array<{
    userId: string;
    userName: string;
    activeListCount: number;
    itemsPicked: number;
  }>;
  recentLists: V4PickingList[];
}

interface V4PickerWorkload {
  activeLists: V4PickingList[];
  completedToday: number;
  itemsPickedToday: number;
}

// =============================================================================
// Transform Functions - Picking Lists
// =============================================================================

function transformPickingListItem(v4: V4PickingListItem): PickingListItem {
  return {
    id: v4.id,
    picking_list_id: v4.pickingListId,
    work_order_item_id: v4.workOrderItemId,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    quantity_to_pick: v4.quantityToPick,
    quantity_picked: v4.quantityPicked,
    quantity_short: v4.quantityShort,
    pick_status: v4.pickStatus as PickingListItem['pick_status'],
    location_id: v4.locationId,
    bin_code: v4.binCode,
    zone_code: v4.zoneCode,
    zone_priority: v4.zonePriority,
    lot_number: v4.lotNumber,
    serial_numbers: v4.serialNumbers,
    picked_at: v4.pickedAt,
    picked_by: v4.pickedBy,
    picked_by_name: v4.pickedByName,
    shortage_reason: v4.shortageReason as PickingListItem['shortage_reason'],
    notes: v4.notes,
    sort_order: v4.sortOrder,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

function transformPickingList(v4: V4PickingList): PickingList {
  return {
    id: v4.id,
    picking_number: v4.pickingNumber,
    work_order_id: v4.workOrderId,
    work_order_number: v4.workOrderNumber,
    status: v4.status as PickingList['status'],
    priority: v4.priority as PickingList['priority'],
    assigned_to: v4.assignedTo,
    assigned_to_name: v4.assignedToName,
    started_at: v4.startedAt,
    completed_at: v4.completedAt,
    notes: v4.notes,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    item_count: v4.itemCount,
    items_picked: v4.itemsPicked,
    items: v4.items?.map(transformPickingListItem),
  };
}

// =============================================================================
// API Functions - Picking Lists
// =============================================================================

export async function getPickingLists(filters?: PickingListFilters): Promise<PickingListsResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.work_order_id) params.workOrderId = filters.work_order_id;
  if (filters?.status) params.status = filters.status;
  if (filters?.priority) params.priority = filters.priority;
  if (filters?.assigned_to) params.assignedTo = filters.assigned_to;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;

  const response = await api.get<V4PickingListsResponse>('/v4/wms/picking-lists', params);
  return {
    items: ensureArray(response.items).map(transformPickingList),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getPickingList(id: string): Promise<PickingList> {
  const response = await api.get<V4PickingList>(`/v4/wms/picking-lists/${id}`);
  return transformPickingList(response);
}

export async function getPickingSummary(): Promise<PickingSummary> {
  const response = await api.get<V4PickingSummary>('/v4/wms/picking-lists/summary');
  return {
    by_status: ensureArray(response.byStatus).map(s => ({
      status: s.status as PickingSummary['by_status'][0]['status'],
      count: s.count || 0,
    })),
    today_stats: {
      lists_created: response.todayStats?.listsCreated || 0,
      lists_completed: response.todayStats?.listsCompleted || 0,
      items_picked: response.todayStats?.itemsPicked || 0,
      shortages: response.todayStats?.shortages || 0,
    },
    active_pickers: ensureArray(response.activePickers).map(p => ({
      user_id: p.userId,
      user_name: p.userName,
      active_list_count: p.activeListCount || 0,
      items_picked: p.itemsPicked || 0,
    })),
    recent_lists: ensureArray(response.recentLists).map(transformPickingList),
  };
}

export async function getMyPickingWorkload(): Promise<{ active_lists: PickingList[]; completed_today: number; items_picked_today: number }> {
  const response = await api.get<V4PickerWorkload>('/v4/wms/picking-lists/my-workload');
  return {
    active_lists: ensureArray(response.activeLists).map(transformPickingList),
    completed_today: response.completedToday || 0,
    items_picked_today: response.itemsPickedToday || 0,
  };
}

export async function generatePickingList(input: GeneratePickingListInput): Promise<PickingList> {
  const response = await api.post<V4PickingList>('/v4/wms/picking-lists', {
    workOrderId: input.workOrderId,
    priority: input.priority,
    assignedTo: input.assignedTo,
    assignedToName: input.assignedToName,
    notes: input.notes,
  });
  return transformPickingList(response);
}

export async function generatePickingListForWorkOrder(workOrderId: string): Promise<PickingList> {
  const response = await api.post<V4PickingList>(`/v4/wms/work-orders/${workOrderId}/generate-picking-list`, {});
  return transformPickingList(response);
}

export async function assignPicker(pickingListId: string, input: AssignPickerInput): Promise<PickingList> {
  const response = await api.post<V4PickingList>(`/v4/wms/picking-lists/${pickingListId}/assign`, {
    userId: input.user_id,
    userName: input.user_name,
  });
  return transformPickingList(response);
}

export async function startPicking(pickingListId: string): Promise<PickingList> {
  const response = await api.post<V4PickingList>(`/v4/wms/picking-lists/${pickingListId}/start`, {});
  return transformPickingList(response);
}

export async function completePicking(pickingListId: string): Promise<PickingList & { workOrderId?: string }> {
  const response = await api.post<V4PickingList>(`/v4/wms/picking-lists/${pickingListId}/complete`, {});
  const transformed = transformPickingList(response);
  return { ...transformed, workOrderId: response.workOrderId };
}

export async function cancelPicking(pickingListId: string, reason?: string): Promise<PickingList & { workOrderId?: string }> {
  const response = await api.post<V4PickingList>(`/v4/wms/picking-lists/${pickingListId}/cancel`, { reason });
  const transformed = transformPickingList(response);
  return { ...transformed, workOrderId: response.workOrderId };
}

export async function confirmPick(pickingListId: string, itemId: string, input: ConfirmPickInput): Promise<PickingListItem & { workOrderId?: string }> {
  const response = await api.post<V4PickingListItem & { workOrderId?: string }>(`/v4/wms/picking-lists/${pickingListId}/items/${itemId}/pick`, {
    quantityPicked: input.quantity_picked,
    lotNumber: input.lot_number,
    serialNumbers: input.serial_numbers,
    notes: input.notes,
  });
  const transformed = transformPickingListItem(response);
  return { ...transformed, workOrderId: response.workOrderId };
}

export async function reportShortage(pickingListId: string, itemId: string, input: ReportShortageInput): Promise<PickingListItem> {
  const response = await api.post<V4PickingListItem>(`/v4/wms/picking-lists/${pickingListId}/items/${itemId}/shortage`, {
    shortageReason: input.shortage_reason,
    quantityShort: input.quantity_short,
    notes: input.notes,
  });
  return transformPickingListItem(response);
}

export async function skipPickItem(pickingListId: string, itemId: string, input?: SkipItemInput): Promise<PickingListItem> {
  const response = await api.post<V4PickingListItem>(`/v4/wms/picking-lists/${pickingListId}/items/${itemId}/skip`, {
    notes: input?.notes,
  });
  return transformPickingListItem(response);
}

// =============================================================================
// V4 API Response Types - Packing Slips (camelCase from server)
// =============================================================================

interface V4PackingSlipsResponse {
  items: V4PackingSlip[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4PackingSlip {
  id: string;
  slipNumber: string;
  workOrderId: string;
  workOrderNumber?: string;
  pickingListId?: string;
  pickingNumber?: string;
  mongoPackingSlipId?: string;
  mongoQuoteId?: string;
  quoteNumber?: string;
  status: string;
  shipDate?: string;
  shippedAt?: string;
  deliveredAt?: string;
  carrier?: string;
  serviceType?: string;
  trackingNumber?: string;
  trackingNumbers?: string[];
  shippingCost?: string;
  weightKg?: string;
  dimensions?: V4PackageDimensions;
  packageCount: number;
  shipFromSiteId?: string;
  shipFromAddress?: V4ShippingAddress;
  shipToSiteId?: string;
  shipToAddress?: V4ShippingAddress;
  shipToContact?: V4ContactInfo;
  specialInstructions?: string;
  internalNotes?: string;
  preparedBy?: string;
  preparedByName?: string;
  preparedAt?: string;
  verifiedBy?: string;
  verifiedByName?: string;
  verifiedAt?: string;
  shippedBy?: string;
  shippedByName?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  workOrderTitle?: string;
  workOrderStatus?: string;
  accountName?: string;
  accountId?: string;
  items?: V4PackingSlipItem[];
}

interface V4PackingSlipItem {
  id: string;
  packingSlipId: string;
  workOrderLineId?: string;
  pickingItemId?: string;
  lineNumber: number;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  quantityOrdered: number;
  quantityPacked: number;
  quantityBackordered: number;
  lotNumber?: string;
  serialNumbers?: string[];
  unitWeightKg?: string;
  lineWeightKg?: string;
  packageNumber: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface V4PackageDimensions {
  length: number;
  width: number;
  height: number;
  unit: 'cm' | 'in';
}

interface V4ShippingAddress {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  organisationId?: string;
}

interface V4ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
}

interface V4ShippingSummary {
  pendingShipments: number;
  shippedToday: number;
  shippedThisWeek: number;
  byStatus: Array<{ status: string; count: number }>;
  byCarrier: Array<{ carrier: string; count: number }>;
  recentShipments: Array<{
    id: string;
    slipNumber: string;
    workOrderNumber?: string;
    status: string;
    carrier?: string;
    trackingNumber?: string;
    shippedAt?: string;
  }>;
}

// =============================================================================
// Transform Functions - Packing Slips
// =============================================================================

function transformPackingSlipItem(v4: V4PackingSlipItem): PackingSlipItem {
  return {
    id: v4.id,
    packing_slip_id: v4.packingSlipId,
    work_order_line_id: v4.workOrderLineId,
    picking_item_id: v4.pickingItemId,
    line_number: v4.lineNumber,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    quantity_ordered: v4.quantityOrdered || 0,
    quantity_packed: v4.quantityPacked || 0,
    quantity_backordered: v4.quantityBackordered || 0,
    lot_number: v4.lotNumber,
    serial_numbers: v4.serialNumbers,
    unit_weight_kg: safeParseFloat(v4.unitWeightKg, { min: 0 }),
    line_weight_kg: safeParseFloat(v4.lineWeightKg, { min: 0 }),
    package_number: v4.packageNumber,
    notes: v4.notes,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

function transformShippingAddress(v4: V4ShippingAddress): ShippingAddress {
  return {
    name: v4.name,
    address_line_1: v4.addressLine1,
    address_line_2: v4.addressLine2,
    city: v4.city,
    state: v4.state,
    postal_code: v4.postalCode,
    country: v4.country,
    organisation_id: v4.organisationId,
  };
}

function transformContactInfo(v4: V4ContactInfo): ContactInfo {
  return {
    name: v4.name,
    phone: v4.phone,
    email: v4.email,
  };
}

function transformDimensions(v4: V4PackageDimensions): PackageDimensions {
  return {
    length: v4.length,
    width: v4.width,
    height: v4.height,
    unit: v4.unit,
  };
}

function transformPackingSlip(v4: V4PackingSlip): PackingSlip {
  return {
    id: v4.id,
    slip_number: v4.slipNumber,
    work_order_id: v4.workOrderId,
    work_order_number: v4.workOrderNumber,
    picking_list_id: v4.pickingListId,
    picking_number: v4.pickingNumber,
    mongo_packing_slip_id: v4.mongoPackingSlipId,
    mongo_quote_id: v4.mongoQuoteId,
    quote_number: v4.quoteNumber,
    status: v4.status as PackingSlip['status'],
    ship_date: v4.shipDate,
    shipped_at: v4.shippedAt,
    delivered_at: v4.deliveredAt,
    carrier: v4.carrier,
    service_type: v4.serviceType,
    tracking_number: v4.trackingNumber,
    tracking_numbers: v4.trackingNumbers,
    shipping_cost: safeParseFloat(v4.shippingCost, { min: 0 }),
    weight_kg: safeParseFloat(v4.weightKg, { min: 0 }),
    dimensions: v4.dimensions ? transformDimensions(v4.dimensions) : undefined,
    package_count: v4.packageCount || 0,
    ship_from_site_id: v4.shipFromSiteId,
    ship_from_address: v4.shipFromAddress ? transformShippingAddress(v4.shipFromAddress) : undefined,
    ship_to_site_id: v4.shipToSiteId,
    ship_to_address: v4.shipToAddress ? transformShippingAddress(v4.shipToAddress) : undefined,
    ship_to_contact: v4.shipToContact ? transformContactInfo(v4.shipToContact) : undefined,
    special_instructions: v4.specialInstructions,
    internal_notes: v4.internalNotes,
    prepared_by: v4.preparedBy,
    prepared_by_name: v4.preparedByName,
    prepared_at: v4.preparedAt,
    verified_by: v4.verifiedBy,
    verified_by_name: v4.verifiedByName,
    verified_at: v4.verifiedAt,
    shipped_by: v4.shippedBy,
    shipped_by_name: v4.shippedByName,
    created_by: v4.createdBy,
    created_by_name: v4.createdByName,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    work_order_title: v4.workOrderTitle,
    work_order_status: v4.workOrderStatus as PackingSlip['work_order_status'],
    account_name: v4.accountName,
    account_id: v4.accountId,
    items: v4.items?.map(transformPackingSlipItem),
  };
}

// =============================================================================
// API Functions - Packing Slips / Shipping
// =============================================================================

export async function getPackingSlips(filters?: PackingSlipFilters): Promise<PackingSlipsResponse> {
  const params: Record<string, unknown> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.work_order_id) params.workOrderId = filters.work_order_id;
  if (filters?.carrier) params.carrier = filters.carrier;
  if (filters?.from_date) params.fromDate = filters.from_date;
  if (filters?.to_date) params.toDate = filters.to_date;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction !== undefined) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4PackingSlipsResponse>('/v4/wms/packing-slips', params);
  return {
    items: ensureArray(response.items).map(transformPackingSlip),
    page_number: response.pageNumber || 1,
    page_length: response.pageLength || 20,
    total: response.total || 0,
  };
}

export async function getPackingSlip(id: string): Promise<PackingSlip> {
  const response = await api.get<V4PackingSlip>(`/v4/wms/packing-slips/${id}`);
  return transformPackingSlip(response);
}

export async function getShippingSummary(): Promise<ShippingSummary> {
  const response = await api.get<V4ShippingSummary>('/v4/wms/packing-slips/summary');
  return {
    pending_shipments: response.pendingShipments || 0,
    shipped_today: response.shippedToday || 0,
    shipped_this_week: response.shippedThisWeek || 0,
    by_status: ensureArray(response.byStatus).map(s => ({
      status: s.status as ShippingSummary['by_status'][0]['status'],
      count: s.count || 0,
    })),
    by_carrier: ensureArray(response.byCarrier).map(c => ({
      carrier: c.carrier,
      count: c.count || 0,
    })),
    recent_shipments: ensureArray(response.recentShipments).map(s => ({
      id: s.id,
      slip_number: s.slipNumber,
      work_order_number: s.workOrderNumber,
      status: s.status as ShippingSummary['recent_shipments'][0]['status'],
      carrier: s.carrier,
      tracking_number: s.trackingNumber,
      shipped_at: s.shippedAt,
    })),
  };
}

export async function createPackingSlip(input: CreatePackingSlipInput): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>('/v4/wms/packing-slips', {
    workOrderId: input.work_order_id,
    shipDate: input.ship_date,
    shipToAddress: input.ship_to_address ? {
      name: input.ship_to_address.name,
      addressLine1: input.ship_to_address.address_line_1,
      addressLine2: input.ship_to_address.address_line_2,
      city: input.ship_to_address.city,
      state: input.ship_to_address.state,
      postalCode: input.ship_to_address.postal_code,
      country: input.ship_to_address.country,
      organisationId: input.ship_to_address.organisation_id,
    } : undefined,
    shipToContact: input.ship_to_contact ? {
      name: input.ship_to_contact.name,
      phone: input.ship_to_contact.phone,
      email: input.ship_to_contact.email,
    } : undefined,
    specialInstructions: input.special_instructions,
    internalNotes: input.internal_notes,
  });
  return transformPackingSlip(response);
}

export async function createPackingSlipFromWorkOrder(workOrderId: string): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>('/v4/wms/packing-slips', { workOrderId });
  return transformPackingSlip(response);
}

export async function updatePackingSlip(id: string, input: UpdatePackingSlipInput): Promise<PackingSlip> {
  const response = await api.put<V4PackingSlip>(`/v4/wms/packing-slips/${id}`, {
    shipDate: input.ship_date,
    carrier: input.carrier,
    serviceType: input.service_type,
    trackingNumber: input.tracking_number,
    trackingNumbers: input.tracking_numbers,
    shippingCost: input.shipping_cost,
    weightKg: input.weight_kg,
    dimensions: input.dimensions ? {
      length: input.dimensions.length,
      width: input.dimensions.width,
      height: input.dimensions.height,
      unit: input.dimensions.unit,
    } : undefined,
    packageCount: input.package_count,
    shipFromSiteId: input.ship_from_site_id,
    shipFromAddress: input.ship_from_address ? {
      name: input.ship_from_address.name,
      addressLine1: input.ship_from_address.address_line_1,
      addressLine2: input.ship_from_address.address_line_2,
      city: input.ship_from_address.city,
      state: input.ship_from_address.state,
      postalCode: input.ship_from_address.postal_code,
      country: input.ship_from_address.country,
      organisationId: input.ship_from_address.organisation_id,
    } : undefined,
    shipToSiteId: input.ship_to_site_id,
    shipToAddress: input.ship_to_address ? {
      name: input.ship_to_address.name,
      addressLine1: input.ship_to_address.address_line_1,
      addressLine2: input.ship_to_address.address_line_2,
      city: input.ship_to_address.city,
      state: input.ship_to_address.state,
      postalCode: input.ship_to_address.postal_code,
      country: input.ship_to_address.country,
      organisationId: input.ship_to_address.organisation_id,
    } : undefined,
    shipToContact: input.ship_to_contact ? {
      name: input.ship_to_contact.name,
      phone: input.ship_to_contact.phone,
      email: input.ship_to_contact.email,
    } : undefined,
    specialInstructions: input.special_instructions,
    internalNotes: input.internal_notes,
  });
  return transformPackingSlip(response);
}

export async function updatePackingSlipItem(packingSlipId: string, itemId: string, input: UpdatePackingSlipItemInput): Promise<PackingSlipItem> {
  const response = await api.put<V4PackingSlipItem>(`/v4/wms/packing-slips/${packingSlipId}/items/${itemId}`, {
    quantityPacked: input.quantity_packed,
    quantityBackordered: input.quantity_backordered,
    lotNumber: input.lot_number,
    serialNumbers: input.serial_numbers,
    unitWeightKg: input.unit_weight_kg,
    lineWeightKg: input.line_weight_kg,
    packageNumber: input.package_number,
    notes: input.notes,
  });
  return transformPackingSlipItem(response);
}

export async function deletePackingSlip(id: string): Promise<void> {
  await api.delete(`/v4/wms/packing-slips/${id}`);
}

export async function preparePackingSlip(id: string): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>(`/v4/wms/packing-slips/${id}/prepare`, {});
  return transformPackingSlip(response);
}

export async function verifyPackingSlip(id: string): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>(`/v4/wms/packing-slips/${id}/verify`, {});
  return transformPackingSlip(response);
}

export async function shipPackingSlip(id: string, input: ShipPackingSlipInput): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>(`/v4/wms/packing-slips/${id}/ship`, {
    carrier: input.carrier,
    serviceType: input.service_type,
    trackingNumber: input.tracking_number,
    trackingNumbers: input.tracking_numbers,
    shippingCost: input.shipping_cost,
    shipDate: input.ship_date,
  });
  return transformPackingSlip(response);
}

export async function confirmDelivery(id: string): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>(`/v4/wms/packing-slips/${id}/deliver`, {});
  return transformPackingSlip(response);
}

export async function cancelPackingSlip(id: string, reason?: string): Promise<PackingSlip> {
  const response = await api.post<V4PackingSlip>(`/v4/wms/packing-slips/${id}/cancel`, { reason });
  return transformPackingSlip(response);
}

// =============================================================================
// V4 Receiving Response Types
// =============================================================================

interface V4ReceivingOrdersResponse {
  items: V4ReceivingOrder[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4ReceivingOrder {
  id: string;
  orderNumber: string;
  poNumber?: string;
  supplierName?: string;
  supplierId?: string;
  status: string;
  expectedDate?: string;
  receivedDate?: string;
  notes?: string;
  trackingNumber?: string;
  carrierSupplierId?: string;
  carrier?: string;
  paymentMethod?: string;
  supplierOrderNumber?: string;
  description?: string;
  purchaseUrl?: string;
  requesterUserId?: string;
  requesterName?: string;
  totalCost?: number;
  createdBy: string;
  createdByName?: string;
  // Audit: started by
  startedBy?: string;
  startedByName?: string;
  startedAt?: string;
  // Audit: completed by
  completedBy?: string;
  completedByName?: string;
  completedAt?: string;
  // Audit: cancelled
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
  lines?: V4ReceivingLine[];
  lineCount?: number;
  linesComplete?: number;
  totalExpected?: number;
  totalReceived?: number;
}

interface V4ReceivingLine {
  id: string;
  receivingOrderId: string;
  lineNumber: number;
  mongoSkuId: string;
  skuCode: string;
  skuName?: string;
  expectedQuantity: number;
  receivedQuantity: number;
  rejectedQuantity: number;
  targetLocationId?: string;
  actualLocationId?: string;
  lotNumber?: string;
  expirationDate?: string;
  unitCost?: string;
  lineStatus: string;
  qcStatus: string;
  qcNotes?: string;
  notes?: string;
  receivedAt?: string;
  receivedBy?: string;
  receivedByName?: string;
  createdAt: string;
  updatedAt: string;
  targetLocationCode?: string;
  targetZoneCode?: string;
  actualLocationCode?: string;
  actualZoneCode?: string;
  serials?: V4ReceivingSerial[];
}

interface V4ReceivingSerial {
  id: string;
  receivingLineId: string;
  serialNumber: string;
  macAddress?: string;
  devId?: string;
  serializedItemId?: string;
  deviceStatus: string;
  qcStatus: string;
  qcNotes?: string;
  notes?: string;
  createdAt: string;
}

interface V4ReceivingSummary {
  totalOrders: number;
  byStatus: Array<{
    status: string;
    count: number;
  }>;
  pendingLines: number;
  itemsReceivedToday: number;
  itemsReceivedThisWeek: number;
  expectedToday: number;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    poNumber?: string;
    supplierName?: string;
    status: string;
    expectedDate?: string;
    createdAt: string;
  }>;
}

interface V4CompleteReceivingResult {
  order: V4ReceivingOrder;
  movementsCreated: number;
  serialsCreated: number;
}

// =============================================================================
// Receiving Transform Functions
// =============================================================================

function transformReceivingSerial(v4: V4ReceivingSerial): ReceivingSerial {
  return {
    id: v4.id,
    receiving_line_id: v4.receivingLineId,
    serial_number: v4.serialNumber,
    mac_address: v4.macAddress,
    dev_id: v4.devId,
    serialized_item_id: v4.serializedItemId,
    device_status: v4.deviceStatus as ReceivingSerial['device_status'],
    qc_status: v4.qcStatus as ReceivingSerial['qc_status'],
    qc_notes: v4.qcNotes,
    notes: v4.notes,
    created_at: v4.createdAt,
  };
}

function transformReceivingLine(v4: V4ReceivingLine): ReceivingLine {
  const lineStatus = v4.lineStatus as ReceivingLine['line_status'];
  return {
    id: v4.id,
    receiving_order_id: v4.receivingOrderId,
    line_number: v4.lineNumber,
    mongo_sku_id: v4.mongoSkuId,
    sku_code: v4.skuCode,
    sku_name: v4.skuName,
    // UI convenience aliases
    sku: v4.skuCode,
    description: v4.skuName,
    expected_quantity: v4.expectedQuantity,
    received_quantity: v4.receivedQuantity,
    rejected_quantity: v4.rejectedQuantity || 0,
    target_location_id: v4.targetLocationId,
    actual_location_id: v4.actualLocationId,
    lot_number: v4.lotNumber,
    expiration_date: v4.expirationDate,
    unit_cost: safeParseFloat(v4.unitCost, { min: 0 }),
    line_status: lineStatus,
    // UI convenience alias
    status: lineStatus,
    storage_location: v4.actualLocationCode,
    qc_status: v4.qcStatus as ReceivingLine['qc_status'],
    qc_notes: v4.qcNotes,
    notes: v4.notes,
    received_at: v4.receivedAt,
    received_by: v4.receivedBy,
    received_by_name: v4.receivedByName,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    target_location_code: v4.targetLocationCode,
    target_zone_code: v4.targetZoneCode,
    actual_location_code: v4.actualLocationCode,
    actual_zone_code: v4.actualZoneCode,
    serials: ensureArray(v4.serials).map(transformReceivingSerial),
  };
}

function transformReceivingOrder(v4: V4ReceivingOrder): ReceivingOrder {
  const lineCount = v4.lineCount ?? 0;
  const linesComplete = v4.linesComplete ?? 0;
  return {
    id: v4.id,
    order_number: v4.orderNumber,
    po_number: v4.poNumber,
    supplier_name: v4.supplierName,
    supplier_id: v4.supplierId,
    status: v4.status as ReceivingOrder['status'],
    expected_date: v4.expectedDate,
    received_date: v4.receivedDate,
    notes: v4.notes,
    tracking_number: v4.trackingNumber,
    carrier_supplier_id: v4.carrierSupplierId,
    carrier: v4.carrier,
    payment_method: v4.paymentMethod,
    supplier_order_number: v4.supplierOrderNumber,
    description: v4.description,
    purchase_url: v4.purchaseUrl,
    requester_user_id: v4.requesterUserId,
    requester_name: v4.requesterName,
    total_cost: v4.totalCost,
    created_by: v4.createdBy,
    created_by_name: v4.createdByName,
    // Audit: started by
    started_by: v4.startedBy,
    started_by_name: v4.startedByName,
    started_at: v4.startedAt,
    // Audit: completed by
    completed_by: v4.completedBy,
    completed_by_name: v4.completedByName,
    completed_at: v4.completedAt,
    // Audit: cancelled
    cancelled_at: v4.cancelledAt,
    cancellation_reason: v4.cancellationReason,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    lines: v4.lines?.map(transformReceivingLine),
    line_count: lineCount,
    lines_complete: linesComplete,
    // UI convenience aliases
    total_lines: lineCount,
    received_lines: linesComplete,
    total_expected: v4.totalExpected,
    total_received: v4.totalReceived,
  };
}

function transformReceivingSummary(v4: V4ReceivingSummary): ReceivingSummary {
  // Compute convenience counts from by_status array
  const statusCounts: Record<string, number> = {};
  for (const s of v4.byStatus) {
    statusCounts[s.status] = s.count;
  }

  return {
    total_orders: v4.totalOrders,
    by_status: v4.byStatus.map(s => ({
      status: s.status as ReceivingSummary['by_status'][0]['status'],
      count: s.count,
    })),
    // Convenience counts derived from by_status
    draft_orders: statusCounts['draft'] ?? 0,
    in_progress_orders: statusCounts['in_progress'] ?? 0,
    pending_qc_orders: statusCounts['pending_qc'] ?? 0,
    completed_orders: statusCounts['completed'] ?? 0,
    pending_lines: v4.pendingLines,
    items_received_today: v4.itemsReceivedToday,
    items_received_this_week: v4.itemsReceivedThisWeek,
    expected_today: v4.expectedToday,
    recent_orders: v4.recentOrders.map(o => ({
      id: o.id,
      order_number: o.orderNumber,
      po_number: o.poNumber,
      supplier_name: o.supplierName,
      status: o.status as ReceivingSummary['recent_orders'][0]['status'],
      expected_date: o.expectedDate,
      created_at: o.createdAt,
    })),
  };
}

// =============================================================================
// Receiving API Functions
// =============================================================================

export async function getReceivingOrders(filters?: ReceivingFilters): Promise<ReceivingOrdersResponse> {
  const params: Record<string, string | number> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.po_number) params.poNumber = filters.po_number;
  if (filters?.supplier_id) params.supplierId = filters.supplier_id;
  if (filters?.supplier_name) params.supplierName = filters.supplier_name;
  if (filters?.from_date) params.fromDate = filters.from_date;
  if (filters?.to_date) params.toDate = filters.to_date;
  if (filters?.search) params.search = filters.search;
  if (filters?.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters?.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters?.sort_key) params.sortKey = filters.sort_key;
  if (filters?.sort_direction) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4ReceivingOrdersResponse>('/v4/wms/receiving', params);
  return {
    items: ensureArray(response.items).map(transformReceivingOrder),
    page_number: response.pageNumber ?? 0,
    page_length: response.pageLength ?? 20,
    total: response.total ?? 0,
  };
}

export async function getReceivingOrder(id: string): Promise<ReceivingOrder> {
  const response = await api.get<V4ReceivingOrder>(`/v4/wms/receiving/${id}`);
  return transformReceivingOrder(response);
}

export async function getReceivingSummary(): Promise<ReceivingSummary> {
  const response = await api.get<V4ReceivingSummary>('/v4/wms/receiving/summary');
  return transformReceivingSummary(response);
}

export async function createReceivingOrder(input: CreateReceivingOrderInput): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>('/v4/wms/receiving', {
    poNumber: input.po_number,
    supplierName: input.supplier_name,
    supplierId: input.supplier_id,
    expectedDate: input.expected_date,
    receivedDate: input.received_date,
    notes: input.notes,
    trackingNumber: input.tracking_number,
    carrierSupplierId: input.carrier_supplier_id,
    carrier: input.carrier,
    paymentMethod: input.payment_method,
    supplierOrderNumber: input.supplier_order_number,
    description: input.description,
    purchaseUrl: input.purchase_url,
    requesterUserId: input.requester_user_id,
  });
  return transformReceivingOrder(response);
}

export async function updateReceivingOrder(id: string, input: UpdateReceivingOrderInput): Promise<ReceivingOrder> {
  const body: Record<string, unknown> = {};
  if (input.po_number !== undefined) body.poNumber = input.po_number;
  if (input.supplier_name !== undefined) body.supplierName = input.supplier_name;
  if (input.supplier_id !== undefined) body.supplierId = input.supplier_id;
  if (input.expected_date !== undefined) body.expectedDate = input.expected_date;
  if (input.received_date !== undefined) body.receivedDate = input.received_date;
  if (input.notes !== undefined) body.notes = input.notes;
  if (input.tracking_number !== undefined) body.trackingNumber = input.tracking_number;
  if (input.carrier_supplier_id !== undefined) body.carrierSupplierId = input.carrier_supplier_id;
  if (input.carrier !== undefined) body.carrier = input.carrier;
  if (input.payment_method !== undefined) body.paymentMethod = input.payment_method;
  if (input.supplier_order_number !== undefined) body.supplierOrderNumber = input.supplier_order_number;
  if (input.description !== undefined) body.description = input.description;
  if (input.purchase_url !== undefined) body.purchaseUrl = input.purchase_url;
  if (input.requester_user_id !== undefined) body.requesterUserId = input.requester_user_id;
  const response = await api.put<V4ReceivingOrder>(`/v4/wms/receiving/${id}`, body);
  return transformReceivingOrder(response);
}

export async function deleteReceivingOrder(id: string): Promise<void> {
  await api.delete(`/v4/wms/receiving/${id}`);
}

export async function addReceivingLine(orderId: string, input: AddReceivingLineInput): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>(`/v4/wms/receiving/${orderId}/lines`, {
    mongoSkuId: input.mongo_sku_id,
    skuCode: input.sku_code || input.sku,
    skuName: input.sku_name || input.description,
    expectedQuantity: input.expected_quantity,
    targetLocationId: input.target_location_id,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    unitOfMeasure: input.unit_of_measure,
    qcRequired: input.qc_required,
    notes: input.notes,
  });
  return transformReceivingOrder(response);
}

export async function updateReceivingLine(orderId: string, lineId: string, input: UpdateReceivingLineInput): Promise<ReceivingOrder> {
  const response = await api.put<V4ReceivingOrder>(`/v4/wms/receiving/${orderId}/lines/${lineId}`, {
    expectedQuantity: input.expected_quantity,
    targetLocationId: input.target_location_id,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    notes: input.notes,
  });
  return transformReceivingOrder(response);
}

export async function removeReceivingLine(orderId: string, lineId: string): Promise<ReceivingOrder> {
  const response = await api.delete<V4ReceivingOrder>(`/v4/wms/receiving/${orderId}/lines/${lineId}`);
  return transformReceivingOrder(response);
}

export async function startReceiving(id: string): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>(`/v4/wms/receiving/${id}/start`, {});
  return transformReceivingOrder(response);
}

export async function receiveLine(orderId: string, lineId: string, input: ReceiveLineInput): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>(`/v4/wms/receiving/${orderId}/lines/${lineId}/receive`, {
    receivedQuantity: input.received_quantity,
    rejectedQuantity: input.rejected_quantity,
    actualLocationId: input.actual_location_id,
    lotNumber: input.lot_number,
    expirationDate: input.expiration_date,
    unitCost: input.unit_cost,
    qcRequired: input.qc_required,
    notes: input.notes,
    serials: input.serials?.map(s => ({
      serialNumber: s.serial_number,
      macAddress: s.mac_address,
      devId: s.dev_id,
      deviceStatus: s.device_status,
      notes: s.notes,
    })),
  });
  return transformReceivingOrder(response);
}

export async function updateLineQcStatus(orderId: string, lineId: string, input: UpdateQCStatusInput): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>(`/v4/wms/receiving/${orderId}/lines/${lineId}/qc`, {
    qcStatus: input.qc_status,
    qcNotes: input.qc_notes,
  });
  return transformReceivingOrder(response);
}

export async function completeReceiving(id: string): Promise<CompleteReceivingResult> {
  const response = await api.post<V4CompleteReceivingResult>(`/v4/wms/receiving/${id}/complete`, {});
  return {
    order: transformReceivingOrder(response.order),
    movements_created: response.movementsCreated,
    serials_created: response.serialsCreated,
  };
}

export async function cancelReceiving(id: string, reason?: string): Promise<ReceivingOrder> {
  const response = await api.post<V4ReceivingOrder>(`/v4/wms/receiving/${id}/cancel`, { reason });
  return transformReceivingOrder(response);
}

// =============================================================================
// API Functions - Users (V3)
// =============================================================================

interface V3UsersResponse {
  users: Array<{
    _id: string;
    email: string;
    firstname?: string;
    lastname?: string;
    username?: string;
    enabled: boolean;
  }>;
  pageNumber: number;
  pageLength: number;
}

export async function getWmsUsers(organizationId: string, filters?: WmsUserFilters): Promise<WmsUsersResponse> {
  const params: Record<string, string | number | boolean> = {
    organisation: organizationId,
  };
  if (filters?.search) params.search = filters.search;
  if (filters?.enabled !== undefined) params.enabled = filters.enabled;
  if (filters?.page_number !== undefined) params.page_number = filters.page_number;
  if (filters?.page_length !== undefined) params.page_length = filters.page_length ?? 50;

  const response = await api.get<V3UsersResponse>('/v4/legacy/users/', params);
  return {
    users: ensureArray(response.users).map(u => ({
      _id: u._id,
      email: u.email,
      firstname: u.firstname,
      lastname: u.lastname,
      username: u.username,
      enabled: u.enabled,
    })),
    pageNumber: response.pageNumber || 1,
    pageLength: response.pageLength || 50,
  };
}

// =============================================================================
// API Functions - Suppliers (V4)
// =============================================================================

interface V4SuppliersResponse {
  suppliers: V4Supplier[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4Supplier {
  id: string;
  accountNumber: string;
  name: string;
  description?: string;
  type: 'supplier';
  status: string;
  supplierKind?: string | null;
  hubspotCompanyId?: string;
  gdriveRootFolderId?: string;
  quickbooksVendorId?: string | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  skuCount?: number;
  skus?: { id: string; sku: string }[];
  enabled: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

function transformSupplier(v4: V4Supplier): Supplier {
  return {
    id: v4.id,
    account_number: v4.accountNumber,
    name: v4.name,
    description: v4.description,
    type: 'supplier',
    status: v4.status as Supplier['status'],
    supplier_kind: (v4.supplierKind ?? null) as Supplier['supplier_kind'],
    hubspot_company_id: v4.hubspotCompanyId,
    gdrive_root_folder_id: v4.gdriveRootFolderId,
    quickbooks_vendor_id: v4.quickbooksVendorId ?? null,
    website: v4.website ?? null,
    email: v4.email ?? null,
    phone: v4.phone ?? null,
    sku_count: v4.skuCount ?? 0,
    skus: v4.skus ?? [],
    enabled: v4.enabled,
    tags: v4.tags || [],
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

export async function getSuppliers(filters: SupplierFilters = {}): Promise<SuppliersResponse> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.kind) {
    params.kind = Array.isArray(filters.kind) ? filters.kind.join(',') : filters.kind;
  }
  if (filters.tag) {
    params.tag = Array.isArray(filters.tag) ? filters.tag.join(',') : filters.tag;
  }
  if (filters.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters.sort_key) params.sortKey = filters.sort_key;
  if (filters.sort_direction !== undefined) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4SuppliersResponse>('/v4/suppliers', params);
  return {
    suppliers: ensureArray(response.suppliers).map(transformSupplier),
    page_number: response.pageNumber,
    page_length: response.pageLength,
    total: response.total,
  };
}

export async function getSupplier(id: string): Promise<Supplier> {
  const response = await api.get<{ supplier: V4Supplier }>(`/v4/suppliers/${id}`);
  return transformSupplier(response.supplier);
}

export async function createSupplier(input: CreateSupplierInput): Promise<Supplier> {
  const response = await api.post<{ supplier: V4Supplier }>('/v4/suppliers', {
    name: input.name,
    description: input.description,
    status: input.status,
    supplierKind: input.supplier_kind,
    hubspotCompanyId: input.hubspot_company_id,
    gdriveRootFolderId: input.gdrive_root_folder_id,
    website: input.website,
    email: input.email,
    phone: input.phone,
    tags: input.tags,
  });
  return transformSupplier(response.supplier);
}

export async function updateSupplier(id: string, input: UpdateSupplierInput): Promise<Supplier> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.status !== undefined) body.status = input.status;
  if (input.supplier_kind !== undefined) body.supplierKind = input.supplier_kind;
  if (input.hubspot_company_id !== undefined) body.hubspotCompanyId = input.hubspot_company_id;
  if (input.gdrive_root_folder_id !== undefined) body.gdriveRootFolderId = input.gdrive_root_folder_id;
  if (input.website !== undefined) body.website = input.website;
  if (input.email !== undefined) body.email = input.email;
  if (input.phone !== undefined) body.phone = input.phone;
  if (input.enabled !== undefined) body.enabled = input.enabled;
  if (input.tags !== undefined) body.tags = input.tags;

  const response = await api.put<{ supplier: V4Supplier }>(`/v4/suppliers/${id}`, body);
  return transformSupplier(response.supplier);
}

export async function deleteSupplier(id: string): Promise<{ deleted: boolean; id: string }> {
  return api.delete<{ deleted: boolean; id: string }>(`/v4/suppliers/${id}`);
}

// =============================================================================
// SKU MASTER CATALOG
// =============================================================================

export async function getSkus(filters: SkuFilters = {}): Promise<SkusResponse> {
  // Server caps pageLength at 100. Load all pages and concatenate.
  const PAGE_SIZE = 100;
  const baseParams: Record<string, unknown> = { pageLength: PAGE_SIZE };
  if (filters.activated !== undefined) baseParams.activated = filters.activated;

  const first = await api.get<SkusResponse>('/v4/wms/skus', { ...baseParams, pageNumber: 0 });
  const total = first.total ?? first.skus.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return first;

  const restRequests = Array.from({ length: totalPages - 1 }, (_, i) =>
    api.get<SkusResponse>('/v4/wms/skus', { ...baseParams, pageNumber: i + 1 }),
  );
  const restResponses = await Promise.all(restRequests);
  const all = first.skus.concat(...restResponses.map((r) => r.skus));
  return { skus: all, total, pageNumber: 0, pageLength: all.length };
}

export async function getSkuById(id: string): Promise<{ sku: Sku }> {
  return api.get<{ sku: Sku }>(`/v4/wms/skus/${id}`);
}

export async function createSku(data: CreateSkuInput): Promise<{ sku: Sku }> {
  return api.post<{ sku: Sku }>('/v4/wms/skus', data);
}

export async function updateSku(id: string, data: UpdateSkuInput): Promise<{ sku: Sku }> {
  return api.put<{ sku: Sku }>(`/v4/wms/skus/${id}`, data);
}

export async function deleteSku(id: string): Promise<void> {
  await api.delete(`/v4/wms/skus/${id}`);
}

export async function checkSkuUsage(id: string): Promise<{ isUsed: boolean; usedIn: Array<{ type: string; count: number }> }> {
  return api.get(`/v4/wms/skus/${id}/isused`);
}

export async function replaceSkuBom(id: string, items: Array<{ component_sku_id: string; qty: number }>): Promise<void> {
  await api.put(`/v4/wms/skus/${id}/bom`, { items });
}

export async function replaceSkuSuppliers(id: string, suppliers: Array<{
  organisation_id: string;
  organisation_name?: string;
  supplier_sku?: string;
  link?: string;
  price?: number;
  lead_time_days?: number;
  moq?: number;
}>): Promise<void> {
  await api.put(`/v4/wms/skus/${id}/suppliers`, { suppliers });
}

export async function searchSkusApi(search?: string, limit?: number): Promise<{ skus: SkuSearchResult[] }> {
  return api.get<{ skus: SkuSearchResult[] }>('/v4/wms/skus/search', { search, limit });
}

// =============================================================================
// SKU Photos
// =============================================================================

export async function uploadSkuPhoto(skuId: string, file: File): Promise<{ photo: SkuPhoto }> {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<{ photo: SkuPhoto }>(`/v4/wms/skus/${skuId}/photos`, formData);
}

export async function deleteSkuPhoto(skuId: string, photoId: string): Promise<void> {
  await api.delete(`/v4/wms/skus/${skuId}/photos/${photoId}`);
}

export async function reorderSkuPhotos(skuId: string, photoIds: string[]): Promise<void> {
  await api.put<void>(`/v4/wms/skus/${skuId}/photos/reorder`, { photoIds });
}
